import mapValues from "lodash/mapValues";
import pickBy from "lodash/pickBy";
import isEqual from "lodash/isEqual";

export const defaultValues = {
  groupByBuilding: true,
  maxDaysOld: 30,
  numEmployees: "",
  neighborhoods: [],
  signUpType: "progressive",
  minMonthlyPrice: 0,
  maxMonthlyPrice: 50000,
  minSqft: 1,
  maxSqft: 50000,
  minOccupancy: 1,
  maxOccupancy: 200,
  minPricePerSquareFoot: 0,
  maxPricePerSquareFoot: 125,
  phoneRequired: false,
  leaseTermMin: null,
  leaseTermMax: null,
  leaseTypes: [],
  submarkets: [],
  area: "",
  market: {
    name: "New York",
    seoString: "ny/new-york",
    externalId: "635d40e4-ee2c-4144-8c95-040714888811",
  },
  limit: 24, // alias for page size
  page: 1,
  count: 0, // total amount of listings returned in set pre page division
  listings: [],
  activeSizeFilter: "SQUARE_FEET",
  activePriceFilter: "MONTHLY",
  promo: "",
};

const filterKeys = [
  "maxDaysOld",
  "minSqft",
  "maxSqft",
  "minOccupancy",
  "maxOccupancy",
  "minMonthlyPrice",
  "maxMonthlyPrice",
  "minPricePerSquareFoot",
  "maxPricePerSquareFoot",
  "leaseTermMin",
  "leaseTermMax",
  "leaseTypes",
  "neighborhoods",
  "submarkets",
  "activeSizeFilter",
  "activePriceFilter",
];

const blockedQueryParamList = [
  "area",
  "market",
  "listings",
  "count",
  "groupByBuilding",
];

const firstValue = (value) => (Array.isArray(value) ? value[0] : value);
const integer =
  (key, minimum = 0) =>
  (value) => {
    const scalar = firstValue(value);
    const parsed = Number(scalar);
    return scalar !== null &&
      scalar !== undefined &&
      scalar !== "" &&
      Number.isSafeInteger(parsed) &&
      parsed >= minimum
      ? parsed
      : defaultValues[key];
  };
const list = (value) =>
  (Array.isArray(value) ? value : [value])
    .filter((entry) => typeof entry === "string")
    .flatMap((entry) => entry.split(","))
    .filter(Boolean);

export const deserializers = {
  maxDaysOld: integer("maxDaysOld"),
  neighborhoods: list,
  submarkets: list,
  minMonthlyPrice: integer("minMonthlyPrice"),
  maxMonthlyPrice: integer("maxMonthlyPrice"),
  minSqft: integer("minSqft", 1),
  maxSqft: integer("maxSqft", 1),
  minOccupancy: integer("minOccupancy", 1),
  maxOccupancy: integer("maxOccupancy", 1),
  minPricePerSquareFoot: integer("minPricePerSquareFoot"),
  maxPricePerSquareFoot: integer("maxPricePerSquareFoot"),
  phoneRequired: (value) => firstValue(value) === "true",
  groupByBuilding: (value) => firstValue(value) === "true",
  leaseTermMin: integer("leaseTermMin"),
  leaseTermMax: integer("leaseTermMax"),
  leaseTypes: list,
  limit: integer("limit", 1),
  page: integer("page", 1),
  count: integer("count"),
  promo: firstValue,
  activePriceFilter: (value) =>
    ["MONTHLY", "PSF"].includes(firstValue(value))
      ? firstValue(value)
      : defaultValues.activePriceFilter,
  activeSizeFilter: (value) =>
    ["SQUARE_FEET", "SEATS"].includes(firstValue(value))
      ? firstValue(value)
      : defaultValues.activeSizeFilter,
};

export const serializers = {
  neighborhoods: (neighborhoods) =>
    neighborhoods
      .map((nhood) => nhood.externalId)
      .filter((nhood) => nhood !== "")
      .join(","),
  submarkets: (submarkets) =>
    submarkets
      .map((submarket) => submarket.externalId)
      .filter(Boolean)
      .join(","),
  listings: () => "",
  promo: (promo) => promo || "",
};

export const keysAreDefault = (keys, objectToCompare) => {
  const currentDefaultValues = pickBy(objectToCompare, (query, key) => {
    return isEqual(defaultValues[key], query);
  });

  const defaultKeys = Object.keys(currentDefaultValues);

  return keys.every((key) => defaultKeys.includes(key));
};

// This will only write the values that are explicitly provided by the user
// to the query string.
//
// We don't need to write values that are currently defaulted as if they remain null
// they'll be populated with their default values again anyway.
//
// This method will also not mess with query string keys that the AdSearch page is not
// aware of, which might prevent headaches down the road.
export const update = (
  newValues,
  currentValues,
  callback,
  queryStringHandler
) => {
  const newValuesObject = {
    ...currentValues,
    ...newValues,
  };
  /* delete groupByBuilding and determine it later in this function */
  delete newValuesObject.groupByBuilding;
  const nonDefaultValues = pickBy(
    newValuesObject,
    (query, key) => !isEqual(defaultValues[key], query)
  );

  const unneededValuesFilter = (queryStringKey) => {
    const value = newValuesObject[queryStringKey];
    const defaultValueKeys = Object.keys(defaultValues);
    // Remove values that are equal to the default to make the query
    // string less crowded.
    if (defaultValues[queryStringKey] === value) return false;
    if (typeof value === "string" && value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (!defaultValueKeys.includes(queryStringKey)) return false;

    return true;
  };
  if (
    filterKeys.some(
      (key) => key in newValues && !isEqual(newValues[key], currentValues[key])
    )
  ) {
    newValuesObject.page = 1;
    delete nonDefaultValues.page;
  }
  const areas = [
    ...newValuesObject.neighborhoods,
    ...newValuesObject.submarkets,
  ];

  newValuesObject.area =
    areas.length === 1
      ? areas[0].externalId
      : newValuesObject.market.externalId;
  callback(newValuesObject);

  let url = "";
  if (areas.length === 1) {
    url = `${areas[0].seoString}/${areas[0].externalId}`;
    delete nonDefaultValues.submarkets;
    delete nonDefaultValues.neighborhoods;
  } else {
    url = `${newValuesObject.market.seoString}/${newValuesObject.market.externalId}`;
    if (areas.length === 0) {
      delete nonDefaultValues.submarkets;
      delete nonDefaultValues.neighborhoods;
    }
  }

  const newQueryObj = Object.keys(nonDefaultValues)
    .filter(unneededValuesFilter)
    .filter((key) => !blockedQueryParamList.includes(key))
    .reduce((acc, key) => {
      acc[key] =
        key in serializers
          ? serializers[key](nonDefaultValues[key])
          : nonDefaultValues[key];

      return acc;
    }, {});

  const groupByBuilding = !Object.keys(newQueryObj).some((key) =>
    filterKeys.includes(key)
  );
  newQueryObj.groupByBuilding = groupByBuilding;

  newQueryObj.slug = url.split("/");
  queryStringHandler(newQueryObj);
};

// ===
// below is commented out. Not needed for public but keeping it around for when we move signed ICE search.
// ===

// export const getSearchFromActiveRequirements = (
//   activeRequirements,
//   cityOfInterest = defaultValues.city
// ) => {
//   if (!activeRequirements) return "";
//   const activeRequirementKeyMap = {
//     minSquareFeet: "minSqft",
//     maxSquareFeet: "maxSqft",
//     numberOfEmployees: "numEmployees",
//   };
//   // prioritise monthly budget over psf
//   const minMonthlyBudgetIsDefault =
//     activeRequirements.minMonthlyBudget === defaultValues.minMonthlyPrice;
//   const maxMonthlyBudgetIsDefault =
//     activeRequirements.maxMonthlyBudget === defaultValues.maxMonthlyPrice;
//   if (
//     (activeRequirements.minMonthlyBudget && !minMonthlyBudgetIsDefault) ||
//     (activeRequirements.maxMonthlyBudget && !maxMonthlyBudgetIsDefault)
//   ) {
//     activeRequirementKeyMap.minMonthlyBudget = "minMonthlyPrice";
//     activeRequirementKeyMap.maxMonthlyBudget = "maxMonthlyPrice";
//   } else {
//     activeRequirementKeyMap.minPsf = "minPricePerSquareFoot";
//     activeRequirementKeyMap.maxPsf = "maxPricePerSquareFoot";
//   }
//   const leaseTermValueMap = {
//     LESS_THAN_ONE_YEAR: "&leaseTermMax=12&leaseTermMin=null",
//     ONE_TO_TWO_YEARS: "&leaseTermMax=36&leaseTermMin=12",
//     THREE_TO_FIVE_YEARS: "&leaseTermMax=60&leaseTermMin=36",
//     MORE_THAN_FIVE_YEARS: "&leaseTermMin=60",
//     FLEXIBLE: "",
//     [null]: "", // this kv pair is used to handle when lease term is null in active requirements.
//   };
//   const searchPath = Object.entries(activeRequirements)
//     .filter(([, value]) => value)
//     .map(([key, value]) =>
//       activeRequirementKeyMap[key]
//         ? `${activeRequirementKeyMap[key]}=${value}`
//         : ""
//     )
//     .filter(Boolean)
//     .join("&");

//   const submarkets = activeRequirements.areas.filter(
//     ({ __typename }) => __typename === "Submarket"
//   );
//   const marketArea = activeRequirements.areas.filter(
//     ({ __typename }) => __typename === "Market"
//   );
//   const nhoods = activeRequirements.areas.filter(
//     ({ __typename }) => __typename === "Neighborhood"
//   );
//   const nhoodsQueryString =
//     nhoods.length > 0
//       ? `neighborhoods=${nhoods.map((nhood) => nhood.name).join(",")}&`
//       : "";
//   const submarketUuids = submarkets.map(({ externalId }) => externalId);
//   const submarketNames = submarkets.map(({ name }) => name);
//   const marketUuid =
//     marketArea?.[0]?.externalId || activeRequirements.market.externalId;
//   const marketName =
//     marketArea?.[0]?.name ||
//     activeRequirements?.market?.name ||
//     defaultValues.market;
//   const neighborhoodUuids = nhoods.map(({ externalId }) => externalId);

//   return nhoodsQueryString
//     .concat(
//       submarketUuids.length > 0
//         ? `submarketUuids=${submarketUuids.join(",")}&`
//         : ""
//     )
//     .concat(
//       submarketNames.length > 0
//         ? `submarketNames=${submarketNames.join(",")}&`
//         : ""
//     )
//     .concat(marketUuid ? `marketUuid=${marketUuid}&` : "")
//     .concat(
//       neighborhoodUuids.length > 0
//         ? `neighborhoodUuids=${neighborhoodUuids.join(",")}&`
//         : ""
//     )
//     .concat(`${searchPath}`)
//     .concat(leaseTermValueMap[activeRequirements.leaseTerm])
//     .concat(cityOfInterest ? `&city=${cityOfInterest}` : "")
//     .concat(`&market=${marketName}`);
// };

export default function getQueryManager({
  queryObj,
  city = defaultValues.city,
  market = defaultValues.market,
}) {
  const queryObject = { ...queryObj };

  // if queryObject city or market is falsy then set it to param vale
  if (!queryObject.city) queryObject.city = city;
  if (!queryObject.market) queryObject.market = market;
  const parsedQueryObject = {
    // Populate the query object with default values which may be overwritten later.
    ...defaultValues,
  };

  const defaultKeys = Object.keys(defaultValues);

  Object.keys(queryObject).forEach((key) => {
    if (key === undefined || key === "") return;
    if (!defaultKeys.includes(key)) return;
    const deserializedValue =
      key in deserializers
        ? deserializers[key](queryObject[key])
        : queryObject[key];

    parsedQueryObject[key] = deserializedValue;
  });

  if (
    !queryObject.activePriceFilter &&
    (queryObject.minPricePerSquareFoot !== undefined ||
      queryObject.maxPricePerSquareFoot !== undefined)
  ) {
    parsedQueryObject.activePriceFilter = "PSF";
  }
  if (
    !queryObject.activeSizeFilter &&
    (queryObject.minOccupancy !== undefined ||
      queryObject.maxOccupancy !== undefined)
  ) {
    parsedQueryObject.activeSizeFilter = "SEATS";
  }

  const groupByBuilding = !Object.keys(queryObject).some((key) =>
    filterKeys.includes(key)
  );

  parsedQueryObject.groupByBuilding = groupByBuilding;

  return {
    defaultValues,
    values: mapValues(parsedQueryObject, (query) => query),
  };
}
